/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch } from '../../store';
import { ProcessService } from '../../services/api/processService';
import { ArtifactService } from '../../services/api/artifactService';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { ProcessTaskService } from '../../services/processTaskService';
import { apiClient } from '../../services/apiClient';
import CreateCompanyProcessDialog from './CreateCompanyProcessDialog';
import { PrimeArtifactState } from '../../types/primeModel';
import {
  ProcessType,
  DemandFlowType,
  DemandType,
  ArtifactStateTransitionType,
} from '../../types/enums';
import { CompanyArtifactId, CArtifact } from '../../types/artifacts-branded';
import { API_BASE_URL } from '../../config';
import styles from './UnifiedProcessManager.module.css';
import { canUsePrimeArtifactFeatures } from '../AppShell/appNavAccess';
import { useAppNavAccess } from '../AppShell/useAppNavAccess';

interface UnifiedProcessManagerProps {
  artifactId: CompanyArtifactId;
  usePrime: boolean;
  artifact: CArtifact | null;
}

interface Process {
  id: string;
  name: string;
  description?: string;
  process_type: string;
  demand_flow_type: string;
  demand_type: string;
  is_prime?: boolean;
  prime_artifact_ref?: string;
  artifact_ref?: string;
  created_at?: string;
  updated_at?: string;
}

export const UnifiedProcessManager: React.FC<UnifiedProcessManagerProps> = ({
  artifactId,
  usePrime,
  artifact,
}) => {
  // Only log in development mode and sample to reduce verbosity
  if (process.env.NODE_ENV === 'development' && Math.random() < 0.05) {
    console.log('[UnifiedProcessManager] Component props (sampled):', {
      artifactId,
      usePrime,
      artifactPrimeId: artifact?.prime_artifact_id,
    });
  }

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const buildApiUrl = (path: string) => `${API_BASE_URL}${path}`;
  const [processes, setProcesses] = useState<Process[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState<Process | null>(null);
  const [showCreateProcessDialog, setShowCreateProcessDialog] = useState(false);
  const [availableStates, setAvailableStates] = useState<PrimeArtifactState[]>([]);
  const navAccess = useAppNavAccess();
  const canApplyFromPrime = canUsePrimeArtifactFeatures(navAccess);
  const fetchAvailableStates = useCallback(async () => {
    // Only log in development mode and sample to reduce verbosity
    if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
      console.log('[UnifiedProcessManager] fetchAvailableStates called (sampled)');
    }

    try {
      let states: PrimeArtifactState[] = [];

      if (usePrime) {
        // For Prime processes, get Prime states
        if (artifact?.prime_artifact_id) {
          try {
            states = await PrimeArtifactService.getPrimeArtifactStates(artifact.prime_artifact_id);
          } catch (primeErr) {
            console.warn('[UnifiedProcessManager] Failed to fetch prime states:', primeErr);
          }
        }
      } else {
        // For Company processes, get Company states only
        if (artifact?.id) {
          try {
            const regularStates = await ArtifactService.getStates(artifact.id);
            console.log('[UnifiedProcessManager] Fetched company states:', regularStates);
            // Convert company states to prime states format for consistency
            states = regularStates.map((state) => ({
              id: state.id,
              name: state.name,
              description: state.description || null,
              prime_artifact_ref: artifact.prime_artifact_id || artifact.id,
              is_required: state.is_required,
              state_type: state.state_type as ArtifactStateTransitionType,
              is_initial: state.is_initial,
              is_final: state.is_final,
              is_improvement: state.is_improvement,
              is_resource_active: state.is_resource_active || false,
              demand_type: state.demand_type as DemandType,
              demand_flow_type: state.demand_flow_type as DemandFlowType,
              state_metadata: state.state_metadata || {},
              state_rules: state.state_rules || {},
              state_parameters: state.state_parameters || {},
              validation_rules: state.validation_rules || {},
              improvement_ref: null,
              improvement_parameters: {},
              created_at: new Date().toISOString(),
              updated_at: null,
            }));
          } catch (regularErr) {
            console.warn('[UnifiedProcessManager] Failed to fetch company states:', regularErr);
          }
        }
      }

      // Only log in development mode and sample
      if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
        console.log('[UnifiedProcessManager] Final states to set (sampled):', states.length);
      }
      setAvailableStates(states);
    } catch (err) {
      console.error('Error fetching available states:', err);
    }
  }, [usePrime, artifact]);

  const fetchProcesses = useCallback(async () => {
    // Only log in development mode and sample
    if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
      console.log('[UnifiedProcessManager] 🚀 fetchProcesses STARTED (sampled)');
    }
    setLoading(true);
    setError(null);
    try {
      let fetchedProcesses: Process[] = [];

      // Only log in development mode and sample
      if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
        console.log('[UnifiedProcessManager] fetchProcesses called (sampled)');
      }

      if (artifact?.id) {
        // Always fetch artifact processes from the artifact page
        const startTime = Date.now();
        // Use the new ProcessTaskService for company processes
        const processTaskService = new ProcessTaskService('company');
        const artifactProcesses = await processTaskService.getProcessesForArtifact(artifact.id);
        const endTime = Date.now();

        // Only log in development mode and sample
        if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
          console.log('[UnifiedProcessManager] ✅ API call completed (sampled) in', endTime - startTime, 'ms');
        }
        console.log(
          '[UnifiedProcessManager] 📊 Number of processes received:',
          artifactProcesses.length
        );

        // ProcessTaskService returns ProcessTask objects, so we need to map them to the Process interface
        fetchedProcesses = artifactProcesses.map((process) => ({
          id: process.id,
          name: process.name,
          description: process.description,
          process_type: process.process_type,
          demand_flow_type: process.demand_flow_type,
          demand_type: process.demand_type,
          is_active: process.is_active,
          created_at: process.created_at,
          updated_at: process.updated_at,
          is_prime: false, // All processes from artifact page are company processes
        }));
        console.log(
          '[UnifiedProcessManager] 🔄 Mapped artifact processes with is_prime=false:',
          fetchedProcesses
        );
      } else {
        // No artifact selected
        console.log('[UnifiedProcessManager] ⚠️ No artifact selected, setting empty processes');
        fetchedProcesses = [];
      }

      setProcesses(fetchedProcesses);
      // Only log in development mode and sample
      if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
        console.log('[UnifiedProcessManager] ✅ fetchProcesses COMPLETED (sampled)');
      }
    } catch (err) {
      console.error('[UnifiedProcessManager] ❌ Error fetching processes:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch processes');
    } finally {
      setLoading(false);
      // Only log in development mode and sample
      if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
        console.log('[UnifiedProcessManager] 🏁 fetchProcesses FINISHED (sampled)');
      }
    }
  }, [usePrime, artifact?.prime_artifact_id, artifact?.id, artifact?.name]);

  // Fetch processes when component mounts or artifact changes
  useEffect(() => {
    // Only log in development mode and sample
    if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
      console.log('[UnifiedProcessManager] 🔄 useEffect triggered (sampled)');
    }
    void fetchProcesses();
    if (usePrime) {
      void fetchAvailableStates();
    }
  }, [
    artifactId,
    usePrime,
    artifact?.prime_artifact_id,
    artifact?.id,
    artifact?.name,
    fetchProcesses,
    fetchAvailableStates,
  ]);

  const handleProcessCreate = async (processData: Partial<Process>) => {
    try {
      setLoading(true);
      let newProcess: Process;

      if (usePrime && artifact?.prime_artifact_id) {
        // Create prime process
        const primeProcess = await ProcessService.create({
          ...processData,
          process_type: processData.process_type as ProcessType,
          demand_flow_type: processData.demand_flow_type as DemandFlowType,
          demand_type: processData.demand_type as DemandType,
          prime_artifact_ref: artifact.prime_artifact_id,
        });
        newProcess = { ...primeProcess, is_prime: true };
      } else {
        // Create regular process
        // TODO: Implement regular process creation
        throw new Error('Regular process creation not yet implemented');
      }

      setProcesses((prev) => [...prev, newProcess]);
      setShowCreateForm(false);
    } catch (err) {
      console.error('Error creating process:', err);
      setError(err instanceof Error ? err.message : 'Failed to create process');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessUpdate = async (processId: string, processData: Partial<Process>) => {
    try {
      setLoading(true);
      let updatedProcess: Process;

      if (usePrime && artifact?.prime_artifact_id) {
        // Update prime process
        const primeProcess = await ProcessService.update(processId, {
          ...processData,
          process_type: processData.process_type as ProcessType,
          demand_flow_type: processData.demand_flow_type as DemandFlowType,
          demand_type: processData.demand_type as DemandType,
          prime_artifact_ref: artifact.prime_artifact_id,
        });
        updatedProcess = { ...primeProcess, is_prime: true };
      } else {
        // Update regular process
        // TODO: Implement regular process update
        throw new Error('Regular process update not yet implemented');
      }

      setProcesses((prev) => prev.map((p) => (p.id === processId ? updatedProcess : p)));
      setSelectedProcess(null);
    } catch (err) {
      console.error('Error updating process:', err);
      setError(err instanceof Error ? err.message : 'Failed to update process');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFromPrime = async () => {
    if (!artifact?.prime_artifact_id) {
      setError('This artifact is not linked to a prime artifact. Cannot apply from prime.');
      return;
    }

    const confirmed = window.confirm(
      'This will apply all processes from the linked prime artifact to this company artifact.\n\nAre you sure you want to continue?'
    );

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.post(
        `/artifacts/${artifactId}/apply-prime-processes`,
        null,
        { params: { prime_artifact_id: artifact.prime_artifact_id } }
      );
      
      // Refresh processes after application
      await fetchProcesses();
      
      alert(`Prime processes applied successfully! Created ${response.data.processes_created || 0} processes.`);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to apply prime processes';
      setError(errorMessage);
      console.error('Error applying prime processes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessDelete = async (processId: string) => {
    try {
      setLoading(true);

      if (usePrime && artifact?.prime_artifact_id) {
        // Delete prime process
        await ProcessService.delete(processId);
      } else {
        // Delete regular process
        // TODO: Implement regular process deletion
        throw new Error('Regular process deletion not yet implemented');
      }

      setProcesses((prev) => prev.filter((p) => p.id !== processId));
      setSelectedProcess(null);
    } catch (err) {
      console.error('Error deleting process:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete process');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessSelect = (process: Process) => {
    setSelectedProcess(process);
  };

  const handleOpenProcessPage = (process: Process) => {
    // All processes from artifact page should navigate to company process details page
    console.log('[UnifiedProcessManager] Opening process page for process:', process);
    console.log(
      '[UnifiedProcessManager] Navigating to Company Process Details page:',
      `/company-processes/${process.id}`
    );
    navigate(`/company-processes/${process.id}`);
  };

  const handleCreateProcess = () => {
    setShowCreateProcessDialog(true);
  };

  const handleCompanyProcessCreated = (processId: string) => {
    console.log('Company process created with ID:', processId);
    // Refresh the processes list
    void fetchProcesses();
    // Close the dialog
    setShowCreateProcessDialog(false);
    // Navigate to the company process details page
    navigate(`/company-processes/${processId}`);
  };

  const handleProcessCreated = async (processData: {
    name: string;
    description: string;
    processType: ProcessType;
    demandFlowType: DemandFlowType;
    demandType: DemandType;
    initialStateId: string;
    endStateId: string;
  }) => {
    try {
      setLoading(true);

      // Create ArtifactProcess based on usePrime setting
      let artifactProcess;

      if (usePrime && artifact?.prime_artifact_id) {
        // usePrime = true: Prime model elements exist, user selects from existing ones
        console.log(
          '[UnifiedProcessManager] usePrime=true: Prime model elements exist, user should select from existing ones'
        );

        // TODO: In the future, we should show a dialog to select from existing prime processes
        // For now, we'll create a new prime process as a fallback
        // This should be replaced with a selection dialog

        const processDataToSend = {
          name: processData.name,
          description: processData.description,
          process_type: processData.processType,
          demand_flow_type: processData.demandFlowType,
          demand_type: processData.demandType,
          prime_artifact_id: artifact.prime_artifact_id, // Backend expects prime_artifact_id, not prime_artifact_ref
          process_parameters: {},
          demand_calculation_rules: {},
          validation_rules: {},
          improvement_parameters: {},
        };

        // Only log in development mode and sample
        if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
          console.log('[UnifiedProcessManager] Creating PrimeProcess (sampled)');
        }
        const primeProcess = await ProcessService.create(processDataToSend);

        // Create the initial transition in the prime process template (optional - skip if it fails)
        console.log(
          '[UnifiedProcessManager] Skipping transition creation for now to focus on process creation'
        );
        // TODO: Re-enable transition creation once process creation is working
        // try {
        //   await PrimeArtifactService.createPrimeArtifactTransition(artifact.prime_artifact_id, {
        //     name: `${processData.name} - Initial Transition`,
        //     description: `Initial transition for ${processData.name}`,
        //     prime_artifact_ref: artifact.prime_artifact_id,
        //     from_state_ref: processData.initialStateId,
        //     to_state_ref: processData.endStateId,
        //     prime_process_ref: primeProcess.id,
        //     default_transition_type: ArtifactStateTransitionType.PROCESSITEM,
        //     transition_parameters: {},
        //     validation_rules: {},
        //     improvement_ref: null,
        //     improvement_parameters: {},
        //     demand_type: DemandType.TARGET,
        //     demand_flow_type: DemandFlowType.DRUM,
        //   });
        //   console.log('[UnifiedProcessManager] Initial transition created successfully');
        // } catch (transitionError) {
        //   console.warn('[UnifiedProcessManager] Failed to create initial transition:', transitionError);
        //   console.log('[UnifiedProcessManager] Continuing without initial transition...');
        // }

        // Create an ArtifactProcess instance that links to the PrimeProcess template
        const artifactProcessData = {
          name: processData.name,
          description: processData.description,
          artifact_ref: artifact.id,
          prime_process_ref: primeProcess.id,
          process_type: processData.processType,
          demand_flow_type: processData.demandFlowType,
          process_parameters: {},
          validation_rules: {},
          demand_calculation_rules: {},
          improvement_ref: undefined,
          improvement_parameters: {},
          process_status: 'ACTIVE',
          nested_state_machine_ref: undefined,
        };

        console.log(
          '[UnifiedProcessManager] Creating ArtifactProcess with data:',
          artifactProcessData
        );
        artifactProcess = await fetch(buildApiUrl('/processes'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(artifactProcessData),
        }).then((res) => res.json());
      } else {
        // usePrime = false: Prime model elements don't exist, create them automatically
        console.log(
          '[UnifiedProcessManager] usePrime=false: Creating prime model elements automatically'
        );

        // Step 1: Create PrimeProcess template automatically (architecture requires it)
        if (!artifact?.prime_artifact_id) {
          throw new Error('Prime artifact ID is required to create process template');
        }

        const processDataToSend = {
          name: processData.name,
          description: processData.description,
          process_type: processData.processType,
          demand_flow_type: processData.demandFlowType,
          demand_type: processData.demandType,
          prime_artifact_ref: artifact.prime_artifact_id,
          process_parameters: {},
          demand_calculation_rules: {},
          validation_rules: {},
          improvement_parameters: {},
        };

        console.log(
          '[UnifiedProcessManager] Creating PrimeProcess automatically:',
          processDataToSend
        );
        const primeProcess = await ProcessService.create(processDataToSend);

        // Create the initial transition in the prime process template (optional - skip if it fails)
        console.log(
          '[UnifiedProcessManager] Skipping transition creation for now to focus on process creation'
        );
        // TODO: Re-enable transition creation once process creation is working
        // try {
        //   await PrimeArtifactService.createPrimeArtifactTransition(artifact.prime_artifact_id, {
        //     name: `${processData.name} - Initial Transition`,
        //     description: `Initial transition for ${processData.name}`,
        //     prime_artifact_ref: artifact.prime_artifact_id,
        //     from_state_ref: processData.initialStateId,
        //     to_state_ref: processData.endStateId,
        //     prime_process_ref: primeProcess.id,
        //     default_transition_type: ArtifactStateTransitionType.PROCESSITEM,
        //     transition_parameters: {},
        //     validation_rules: {},
        //     improvement_ref: null,
        //     improvement_parameters: {},
        //     demand_type: DemandType.TARGET,
        //     demand_flow_type: DemandFlowType.DRUM,
        //   });
        //   console.log('[UnifiedProcessManager] Initial transition created successfully');
        // } catch (transitionError) {
        //   console.warn('[UnifiedProcessManager] Failed to create initial transition:', transitionError);
        //   console.log('[UnifiedProcessManager] Continuing without initial transition...');
        // }

        // Step 2: Create ArtifactProcess instance that links to the automatically created PrimeProcess
        const artifactProcessData = {
          name: processData.name,
          description: processData.description,
          artifact_ref: artifact.id,
          prime_process_ref: primeProcess.id,
          process_type: processData.processType,
          demand_flow_type: processData.demandFlowType,
          process_parameters: {},
          validation_rules: {},
          demand_calculation_rules: {},
          improvement_ref: undefined,
          improvement_parameters: {},
          process_status: 'ACTIVE',
          nested_state_machine_ref: undefined,
        };

        console.log(
          '[UnifiedProcessManager] Creating ArtifactProcess with automatically created PrimeProcess:',
          artifactProcessData
        );
        artifactProcess = await fetch(buildApiUrl('/processes'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(artifactProcessData),
        }).then((res) => res.json());
      }

      // Refresh the processes list
      await fetchProcesses();

      // Close the dialog
      setShowCreateProcessDialog(false);

      // Navigate to the company process details page (using the artifact process ID)
      console.log(
        '[UnifiedProcessManager] Navigating with ArtifactProcess ID:',
        artifactProcess.id
      );
      navigate(`/company-processes/${artifactProcess.id}`);
    } catch (err: unknown) {
      console.error('Error creating process:', err);

      // Display detailed validation errors if available
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { detail?: unknown } } };
        if (axiosError.response?.data?.detail) {
          if (Array.isArray(axiosError.response.data.detail)) {
            const errorMessages = axiosError.response.data.detail
              .map((error: unknown) => {
                const errorObj = error as { loc?: string[]; msg?: string; message?: string };
                return `${errorObj.loc?.join('.') || 'field'}: ${errorObj.msg || errorObj.message || 'validation error'}`;
              })
              .join('\n');
            setError(`Validation errors:\n${errorMessages}`);
          } else {
            setError(`Server error: ${String(axiosError.response.data.detail)}`);
          }
        }
      } else {
        setError(err instanceof Error ? err.message : 'Failed to create process');
      }
    } finally {
      setLoading(false);
    }
  };

  // Only log in development mode and sample to reduce verbosity
  if (process.env.NODE_ENV === 'development' && Math.random() < 0.05) {
    console.log('[UnifiedProcessManager] 🎨 RENDERING (sampled):', {
      loading,
      error: !!error,
      processesCount: processes.length,
    });
  }

  if (loading) {
    return <div className={styles.unifiedProcessManager}>Loading processes...</div>;
  }

  if (error) {
    return (
      <div className={styles.unifiedProcessManager}>
        <div className={styles.errorMessage}>Error: {error}</div>
        <button onClick={fetchProcesses}>Retry</button>
      </div>
    );
  }

  return (
    <div className={styles.unifiedProcessManager}>
      <div className={styles.processHeader}>
        <h3>Processes</h3>
        <div className={styles.processActions}>
          {artifact?.prime_artifact_id && (
            <button
              onClick={handleApplyFromPrime}
              className={styles.applyFromPrimeButton}
              disabled={loading || !canApplyFromPrime}
              title={
                canApplyFromPrime
                  ? 'Apply all processes from linked prime artifact'
                  : 'Requires prime_artifacts subscription or platform admin access'
              }
            >
              Apply from Prime
            </button>
          )}
          <button className={styles.createButton} onClick={handleCreateProcess} disabled={loading}>
            Create Process
          </button>
        </div>
      </div>

      <div className={styles.processContent}>
        <div className={styles.processList}>
          {processes.length === 0 ? (
            <div className={styles.noProcesses}>
              <p>No processes found.</p>
              <p>Create a process to get started.</p>
            </div>
          ) : (
            processes.map((process) => (
              <div
                key={process.id}
                className={`${styles.processItem} ${selectedProcess?.id === process.id ? styles.selected : ''}`}
                onClick={() => handleProcessSelect(process)}
              >
                <div className={styles.processInfo}>
                  <div className={styles.processName}>
                    {process.name}
                    {process.is_prime && <span className={styles.primeBadge}>Prime</span>}
                  </div>
                  <div className={styles.processDescription}>
                    {process.description || 'No description'}
                  </div>
                  <div className={styles.processMeta}>
                    <span className={styles.processType}>{process.process_type}</span>
                    <span className={styles.demandFlowType}>{process.demand_flow_type}</span>
                  </div>
                </div>
                <div className={styles.processActions}>
                  <button
                    className={styles.openButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenProcessPage(process);
                    }}
                  >
                    Open Process
                  </button>
                  <button
                    className={styles.editButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProcess(process);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className={styles.deleteButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Are you sure you want to delete this process?')) {
                        void handleProcessDelete(process.id);
                      }
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {selectedProcess && (
          <div className={styles.processDetails}>
            <h4>Process Details</h4>
            <div className={styles.detailRow}>
              <label>Name:</label>
              <span>{selectedProcess.name}</span>
            </div>
            <div className="detail-row">
              <label>Description:</label>
              <span>{selectedProcess.description || 'No description'}</span>
            </div>
            <div className="detail-row">
              <label>Type:</label>
              <span>{selectedProcess.process_type}</span>
            </div>
            <div className="detail-row">
              <label>Demand Flow Type:</label>
              <span>{selectedProcess.demand_flow_type}</span>
            </div>
            {selectedProcess.created_at && (
              <div className="detail-row">
                <label>Created:</label>
                <span>{new Date(selectedProcess.created_at).toLocaleString()}</span>
              </div>
            )}
            {selectedProcess.updated_at && (
              <div className="detail-row">
                <label>Updated:</label>
                <span>{new Date(selectedProcess.updated_at).toLocaleString()}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {showCreateProcessDialog && usePrime && (
        <CreateProcessDialog
          artifact={artifact}
          availableStates={availableStates}
          onProcessCreated={handleProcessCreated}
          onCancel={() => setShowCreateProcessDialog(false)}
          usePrime={usePrime}
        />
      )}

      {showCreateProcessDialog && !usePrime && (
        <CreateCompanyProcessDialog
          artifact={artifact}
          onProcessCreated={handleCompanyProcessCreated}
          onCancel={() => setShowCreateProcessDialog(false)}
        />
      )}

      {showCreateForm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3>Create Process</h3>
            <UnifiedProcessForm
              onSubmit={handleProcessCreate}
              onCancel={() => setShowCreateForm(false)}
              artifactId={artifactId}
              usePrime={usePrime}
            />
          </div>
        </div>
      )}

      {selectedProcess && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3>Edit Process</h3>
            <UnifiedProcessForm
              initialProcess={selectedProcess}
              onSubmit={(data) => handleProcessUpdate(selectedProcess.id, data)}
              onCancel={() => setSelectedProcess(null)}
              artifactId={artifactId}
              usePrime={usePrime}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Process Form Component
interface UnifiedProcessFormProps {
  initialProcess?: Process;
  onSubmit: (data: Partial<Process>) => Promise<void>;
  onCancel: () => void;
  artifactId: string;
  usePrime: boolean;
}

const UnifiedProcessForm: React.FC<UnifiedProcessFormProps> = ({
  initialProcess,
  onSubmit,
  onCancel,
  artifactId,
  usePrime,
}) => {
  const [formData, setFormData] = useState({
    name: initialProcess?.name || '',
    description: initialProcess?.description || '',
    process_type: initialProcess?.process_type || ProcessType.DEMAND,
    demand_flow_type: initialProcess?.demand_flow_type || DemandFlowType.DRUM,
    process_parameters: {},
    demand_calculation_rules: {},
    validation_rules: {},
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error saving process:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.processForm}>
      <div className="form-group">
        <label htmlFor="name">Name</label>
        <input
          id="name"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        />
      </div>

      <div className="form-group">
        <label htmlFor="process_type">Process Type</label>
        <select
          id="process_type"
          value={formData.process_type}
          onChange={(e) =>
            setFormData({ ...formData, process_type: e.target.value as ProcessType })
          }
        >
          <option value={ProcessType.DEFAULT}>Default (Unassigned)</option>
          <option value={ProcessType.DEMAND}>Demand</option>
          <option value={ProcessType.SUPPLY}>Supply</option>
          <option value={ProcessType.DELIVERY}>Delivery</option>
          <option value={ProcessType.FINANCE}>Finance</option>
          <option value={ProcessType.HR}>HR</option>
          <option value={ProcessType.IT}>IT</option>
          <option value={ProcessType.STRATEGY}>Strategy</option>
          <option value={ProcessType.IMPROVEMENT}>Improvement</option>
          <option value={ProcessType.QUALITY}>Quality</option>
          <option value={ProcessType.COMPLIANCE}>Compliance</option>
          <option value={ProcessType.RESEARCH}>Research</option>
          <option value={ProcessType.MARKETFIT}>MarketFit</option>
          <option value={ProcessType.FITFINDER}>FitFinder</option>
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="demand_flow_type">Demand Flow Type</label>
        <select
          id="demand_flow_type"
          value={formData.demand_flow_type}
          onChange={(e) =>
            setFormData({ ...formData, demand_flow_type: e.target.value as DemandFlowType })
          }
        >
          <option value={DemandFlowType.DRUM}>Drum</option>
          <option value={DemandFlowType.PUSH}>Push</option>
          <option value={DemandFlowType.PULL}>Pull</option>
        </select>
      </div>

      <div className={styles.formActions}>
        <button type="submit" className={styles.saveButton}>
          {initialProcess ? 'Update' : 'Create'}
        </button>
        <button type="button" onClick={onCancel} className={styles.cancelButton}>
          Cancel
        </button>
      </div>
    </form>
  );
};

// Create Process Dialog Component
interface CreateProcessDialogProps {
  artifact: {
    id: string;
    name: string;
    prime_artifact_id?: string;
  } | null;
  availableStates: PrimeArtifactState[];
  onProcessCreated: (data: {
    name: string;
    description: string;
    processType: ProcessType;
    demandFlowType: DemandFlowType;
    demandType: DemandType;
    initialStateId: string;
    endStateId: string;
  }) => Promise<void>;
  onCancel: () => void;
  usePrime: boolean;
}

const CreateProcessDialog: React.FC<CreateProcessDialogProps> = ({
  artifact,
  availableStates,
  onProcessCreated,
  onCancel,
  usePrime,
}) => {
  console.log('[CreateProcessDialog] Rendering with:', {
    artifact,
    availableStates,
    usePrime,
    statesCount: availableStates.length,
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    processType: ProcessType.DEMAND,
    demandFlowType: DemandFlowType.DRUM,
    demandType: DemandType.SUPPLY,
    initialStateId: '',
    endStateId: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.initialStateId || !formData.endStateId) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onProcessCreated(formData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create process');
    } finally {
      setLoading(false);
    }
  };

  if (!usePrime) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h3>Create Process</h3>
          <div style={{ padding: '1rem', color: '#888' }}>
            Process creation requires Enhanced Prime Features to be enabled.
          </div>
          <div className={styles.formActions}>
            <button type="button" onClick={onCancel} className={styles.cancelButton}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>Create New Process</h3>
        <p>Create a new process for {artifact?.name || 'this artifact'}</p>

        {availableStates.length === 0 && (
          <div className={styles.warningMessage}>
            No states found for this artifact. Please create states first before creating a process.
          </div>
        )}

        {availableStates.length > 0 && (
          <div className="info-message">
            Found {availableStates.length} state(s) available for process creation.
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className={styles.createProcessForm}>
          <div className="form-group">
            <label htmlFor="process-name">Process Name *</label>
            <input
              id="process-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="Enter process name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="process-description">Description</label>
            <textarea
              id="process-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter process description (optional)"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="process-type">Process Type *</label>
            <select
              id="process-type"
              value={formData.processType}
              onChange={(e) =>
                setFormData({ ...formData, processType: e.target.value as ProcessType })
              }
              required
            >
              <option value={ProcessType.DEFAULT}>Default (Unassigned)</option>
              <option value={ProcessType.DEMAND}>Demand</option>
              <option value={ProcessType.SUPPLY}>Supply</option>
              <option value={ProcessType.DELIVERY}>Delivery</option>
              <option value={ProcessType.FINANCE}>Finance</option>
              <option value={ProcessType.HR}>HR</option>
              <option value={ProcessType.IT}>IT</option>
              <option value={ProcessType.STRATEGY}>Strategy</option>
              <option value={ProcessType.IMPROVEMENT}>Improvement</option>
              <option value={ProcessType.QUALITY}>Quality</option>
              <option value={ProcessType.COMPLIANCE}>Compliance</option>
              <option value={ProcessType.RESEARCH}>Research</option>
              <option value={ProcessType.MARKETFIT}>MarketFit</option>
              <option value={ProcessType.FITFINDER}>FitFinder</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="demand-flow-type">Demand Flow Type *</label>
            <select
              id="demand-flow-type"
              value={formData.demandFlowType}
              onChange={(e) =>
                setFormData({ ...formData, demandFlowType: e.target.value as DemandFlowType })
              }
              required
            >
              <option value={DemandFlowType.DRUM}>Drum</option>
              <option value={DemandFlowType.PUSH}>Push</option>
              <option value={DemandFlowType.PULL}>Pull</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="demand-type">Demand Type *</label>
            <select
              id="demand-type"
              value={formData.demandType}
              onChange={(e) =>
                setFormData({ ...formData, demandType: e.target.value as DemandType })
              }
              required
            >
              <option value={DemandType.SUPPLY}>Supply (default)</option>
              <option value={DemandType.TARGET}>Target</option>
              <option value={DemandType.CAPACITY}>Capacity</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="initial-state">Initial State *</label>
            <select
              id="initial-state"
              value={formData.initialStateId}
              onChange={(e) => setFormData({ ...formData, initialStateId: e.target.value })}
              required
            >
              <option value="">Select initial state</option>
              {availableStates.map((state) => (
                <option key={state.id} value={state.id}>
                  {state.name} {state.is_initial && '(Initial)'}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="end-state">End State *</label>
            <select
              id="end-state"
              value={formData.endStateId}
              onChange={(e) => setFormData({ ...formData, endStateId: e.target.value })}
              required
            >
              <option value="">Select end state</option>
              {availableStates.map((state) => (
                <option key={state.id} value={state.id}>
                  {state.name} {state.is_final && '(Final)'}
                </option>
              ))}
            </select>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="create-button"
              disabled={
                loading || !formData.name || !formData.initialStateId || !formData.endStateId
              }
            >
              {loading ? 'Creating...' : 'Create Process'}
            </button>
            <button type="button" onClick={onCancel} className="cancel-button">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UnifiedProcessManager;
